/** Minimal Library row shape used by Current Screensaver labeling. */
export type ScreensaverLabelRow = {
  id: string;
  versionName?: string;
  version?: string;
  filename?: string;
  contentId?: string;
  mediaUrl?: string;
  apkUrl?: string;
};

/** Library / version label for a screensaver registry row. */
export function displayScreensaverName(row: ScreensaverLabelRow): string {
  return row.versionName || row.version || row.filename || row.id;
}

/**
 * Resolve a Library row for durable device active content.
 * Prefer contentId match (package SoT); fall back to release id.
 */
export function findScreensaverForActiveContent(args: {
  activeContentId: string | null | undefined;
  releaseId?: string | null | undefined;
  screensavers: ScreensaverLabelRow[];
}): ScreensaverLabelRow | null {
  const contentId = args.activeContentId?.trim();
  if (contentId) {
    const byContent = args.screensavers.find((s) => s.contentId === contentId);
    if (byContent) return byContent;
    const byId = args.screensavers.find((s) => s.id === contentId);
    if (byId) return byId;
  }
  const releaseId = args.releaseId?.trim();
  if (releaseId) {
    return args.screensavers.find((s) => s.id === releaseId) || null;
  }
  return null;
}

/**
 * Current Screensaver = media actually active on the device package
 * (durable Fleet device fields after device command acknowledgement).
 *
 * NOT Library selection, NOT upload, NOT command publish.
 */
export function formatCurrentScreensaverLabel(args: {
  /** Package-reported active contentId (preferred). */
  activeContentId?: string | null | undefined;
  /** Fleet release id when contentId not yet mapped. */
  releaseId?: string | null | undefined;
  screensavers: ScreensaverLabelRow[];
  /**
   * @deprecated M4c — session-only Apply id. Ignored when activeContentId/releaseId present.
   */
  appliedScreensaverId?: string | null | undefined;
}): string {
  const row = findScreensaverForActiveContent({
    activeContentId: args.activeContentId,
    releaseId: args.releaseId || args.appliedScreensaverId,
    screensavers: args.screensavers,
  });
  if (row) return displayScreensaverName(row);
  const contentId = args.activeContentId?.trim();
  if (contentId) return contentId;
  const releaseId = (args.releaseId || args.appliedScreensaverId)?.trim();
  if (releaseId) return releaseId;
  return "None";
}

/** Browser-only preview URL from registry media fields (CDN). */
export function resolvePreviewMediaUrl(
  row: ScreensaverLabelRow | null | undefined
): string | null {
  if (!row) return null;
  const url = row.mediaUrl || row.apkUrl || null;
  return typeof url === "string" && url.trim() ? url.trim() : null;
}
